import { checkIsCefrLanguageAssessmentFieldName } from "@repo/app-types/entities/assessment";
import { buildInterviewSectionAssessmentCustomFieldName } from "@repo/app-types/entities/customField";
import type { InterviewLoaded } from "@repo/app-types/entities/interview";
import {
  InterviewSectionStatus,
  type AdminApiInterviewSectionResult,
} from "@repo/app-types/entities/interviewSection/result";
import { InterviewSectionType } from "@repo/app-types/entities/interviewSection/types";
import {
  getProjectAndJobTypeProperty,
  JobTypeProperty,
} from "@repo/app-types/entities/project";
import { buildProjectUrlPath } from "@repo/app-types/helpers/adminAppRoutes";
import { getCefrCustomFieldDisplayValue } from "@repo/app-types/helpers/interviewHelpers";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { IconPencil } from "@tabler/icons-react";
import type { FC } from "react";
import { useNavigate } from "react-router";
import { useAppSelector } from "../../../hooks/hook.js";
import { selectAssessmentById } from "../../assessments/assessmentsSlice.js";
import { selectProjectById } from "../../projects/projectsSlice.js";
import { InterviewAssessmentRating } from "./interviewAssessmentRating.js";

interface InterviewAssessmentSectionProps {
  interview: InterviewLoaded;
  interviewSectionResults?: readonly AdminApiInterviewSectionResult[];
}

export const InterviewAssessmentSection: FC<
  InterviewAssessmentSectionProps
> = ({ interview, interviewSectionResults }) => {
  const navigate = useNavigate();

  const project = useAppSelector((state) =>
    selectProjectById(state, interview.project_id),
  );

  const assessment = useAppSelector(
    selectAssessmentById(
      project
        ? getProjectAndJobTypeProperty(project, JobTypeProperty.AssessmentId)
        : undefined,
    ),
  );

  if (!assessment || !project) {
    return null;
  }

  // Prepare all assessment fields to render
  const assessmentFields: {
    id: string;
    displayName: string;
    value: any;
    displayValue?: string;
    fieldName?: string;
  }[] = [];

  // Add special fields for sections first. Canceled attempts have no useful assessment data, so skip them.
  for (const section of interviewSectionResults ?? []) {
    if (section.status === InterviewSectionStatus.Canceled) {
      continue;
    }

    switch (section.settings.type) {
      case InterviewSectionType.Questions:
        // Voice interview assessments are stored on the interview metadata
        break;

      case InterviewSectionType.RolePlay:
      case InterviewSectionType.KnowledgeTest: {
        const sectionDefinitionId = section.interview_section_definition_id;
        const fieldName = sectionDefinitionId
          ? buildInterviewSectionAssessmentCustomFieldName(sectionDefinitionId)
          : undefined;
        const field = interview.custom_fields.find(
          (field) => field.field_name === fieldName,
        );

        if (field) {
          const defaultTitle =
            section.settings.type === InterviewSectionType.RolePlay
              ? "Role play"
              : "Knowledge test";

          assessmentFields.push({
            id: `section:${section.id}`,
            displayName: `${section.settings.title || defaultTitle} assessment`,
            value: field.data.value,
            displayValue: field.data.display_value,
            fieldName: field.field_name,
          });
        }

        break;
      }
      case InterviewSectionType.ChatQuiz:
        // Chat quiz assessments are stored on the interview section result
        break;

      default:
        assertUnreachable(section.settings, () => {});
        break;
    }
  }

  // Add standard assessment fields
  assessment.custom_field_definitions.forEach((fieldDefinition) => {
    const value = interview.custom_fields.find(
      (cf) => cf.custom_field_definition_id === fieldDefinition.id,
    );

    // Special case for CEFR custom field.
    // If the CEFR assessment value is not defined, we still want to display some useful info to the user as to why.
    const isCefrCustomFieldWithNoValue =
      checkIsCefrLanguageAssessmentFieldName(fieldDefinition.field_name) &&
      !value;

    const displayValue = isCefrCustomFieldWithNoValue
      ? getCefrCustomFieldDisplayValue(interview)
      : value?.data.display_value;

    assessmentFields.push({
      id: fieldDefinition.id,
      displayName: fieldDefinition.display_name,
      value: value?.data.value,
      displayValue,
      fieldName: value?.field_name,
    });
  });

  // Render the assessment table
  return (
    <div className="mt-4 overflow-hidden border border-border-light sm:rounded-lg">
      <table className="w-full min-w-full table-fixed divide-y divide-border-light">
        <thead className="group bg-gray-50">
          <tr>
            <th
              scope="col"
              className="flex w-[25%] items-center gap-2 px-4 py-2 text-left text-xs font-medium text-muted-foreground"
            >
              Qualification
              <IconButton
                icon={
                  <IconPencil className="h-3 w-3 opacity-0 group-hover:opacity-100" />
                }
                variant={ButtonVariant.Tertiary}
                onClick={() => {
                  navigate(buildProjectUrlPath(project.id));
                }}
              />
            </th>
            <th
              scope="col"
              className="w-[15%] px-4 py-2 text-left text-xs font-medium text-muted-foreground"
            >
              Rating
            </th>
            <th
              scope="col"
              className="w-[60%] px-4 py-2 text-left text-xs font-medium text-muted-foreground"
            >
              Comments
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-light bg-white">
          {assessmentFields.length === 0 ? (
            <tr>
              <td
                colSpan={3}
                className="px-4 py-4 text-center text-sm text-foreground"
              >
                No job qualifications added yet.{" "}
                <span
                  onClick={() => {
                    navigate(buildProjectUrlPath(project.id));
                  }}
                  className="cursor-pointer font-medium text-blue-600 hover:text-blue-500"
                >
                  Add criteria.
                </span>
              </td>
            </tr>
          ) : (
            assessmentFields.map((field) => (
              <tr key={field.id} className={`group relative`}>
                <td className={`px-4 py-2 text-sm text-foreground`}>
                  {field.displayName}
                </td>
                <td className="px-4 py-2 text-sm text-foreground">
                  {interview.status === "pending" ? (
                    <span className="text-gray-400">--</span>
                  ) : (
                    <InterviewAssessmentRating
                      rating={field.value}
                      fieldName={field.fieldName}
                    />
                  )}
                </td>
                <td className="px-4 py-2 text-sm text-foreground">
                  {field.displayValue}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
